import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, EnvelopeSimple, Eye, EyeSlash, LockKey, Spinner } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/errors";

export default function CoachLogin() {
  const { coach, signInCoach } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const prefillEmail = new URLSearchParams(loc.search).get("email") || "";
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  // If already signed in, bounce to where they were headed (or dashboard)
  if (coach && coach !== null) {
    const from = loc.state?.from || "/coach/dashboard";
    return <Navigate to={from} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await signInCoach(email.trim(), password);
      toast.success("Welcome back.");
      nav(loc.state?.from || "/coach/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiError(err, "Sign-in failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 sm:px-8 py-16 lg:py-24">
      <div className="anim-in delay-1">
        <div className="label-kicker" style={{ color: "#1E3A2B" }}>Coach Console</div>
        <h1 className="font-serif text-4xl text-[#1E3A2B] mt-3 tracking-tight leading-tight">
          Sign in.
        </h1>
        <p className="text-[#4A524C] mt-3 leading-relaxed">
          For coaches only. Manage your availability and respond to leaders.
        </p>
      </div>
      <form onSubmit={submit} className="mt-8 space-y-4 anim-in delay-2">
        <label className="block">
          <span className="label-kicker block mb-2">Email</span>
          <div className="relative">
            <EnvelopeSimple size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948E]" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="coach-login-email"
              autoFocus
              required
              autoComplete="email"
              className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:ring-2 focus:ring-[#768C7E]/30 focus:outline-none rounded-xl pl-11 pr-4 py-3.5 text-[#121815] placeholder-[#8A948E] transition-all"
              placeholder="you@firm.com"
            />
          </div>
        </label>
        <label className="block">
          <span className="label-kicker block mb-2">Password</span>
          <div className="relative">
            <LockKey size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948E]" />
            <input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="coach-login-password"
              required
              autoComplete="current-password"
              className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:ring-2 focus:ring-[#768C7E]/30 focus:outline-none rounded-xl pl-11 pr-12 py-3.5 text-[#121815] placeholder-[#8A948E] transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              data-testid="coach-login-toggle-pw"
              aria-label={showPw ? "Hide password" : "Show password"}
              tabIndex={-1}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-md text-[#8A948E] hover:text-[#1E3A2B] hover:bg-[#F3F0EA] transition-colors"
            >
              {showPw ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
            </button>
          </div>
        </label>
        <button
          type="submit"
          disabled={busy}
          data-testid="coach-login-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#1E3A2B] text-white px-5 py-3.5 rounded-xl font-medium hover:bg-[#2C4C3B] disabled:opacity-60 transition-colors"
        >
          {busy ? <Spinner size={18} className="animate-spin" /> : <CheckCircle size={18} weight="duotone" />}
          Sign in
        </button>
        <p className="text-xs text-[#8A948E] text-center pt-2">
          Want to apply as a coach? <Link to="/coach/register" className="text-[#1E3A2B] underline">Create coach account →</Link>
        </p>
        <p className="text-xs text-[#8A948E] text-center">
          Not a coach? <Link to="/auth/login" className="text-[#1E3A2B] underline">Leader sign-in →</Link>
        </p>
      </form>
    </div>
  );
}
